import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getClassMemberDisplayNumbers } from '../../utils/classMemberNumbers';
import { createPortal } from 'react-dom';
import CachedProfileImage from '../common/CachedProfileImage';
import MemberProfileImageUploadModal from './MemberProfileImageUploadModal';
import { displayClassMemberName } from '../../utils/classMemberNameDisplay';
import {
  DEFAULT_MEMBER_PROFILE_IMAGE,
  normalizeProfileImageId,
  type ClassMemberRow,
} from '../../utils/classMembersStorage';
import { preloadProfileImages } from '../../utils/profileImageCache';
import {
  preloadMemberPhotoImages,
  useMemberPhotoDisplayUrl,
} from '../../utils/memberPhotoImageCache';
import {
  readMemberProfileDisplayMode,
  writeMemberProfileDisplayMode,
  type MemberProfileDisplayMode,
} from '../../utils/memberProfileDisplay';
import {
  countOrphanMemberProfileAssets,
  deleteMemberPhoto,
  findExistingMemberPhotoPaths,
  uploadMemberPhoto,
} from '../../services/memberProfilePhotoStorageService';
import { updateMemberPhotoProfile } from '../../services/classMembersService';
import ProfileCardCachedImage from '../common/ProfileCardCachedImage';
import {
  fetchMemberProfileCardUnlocks,
  getAdminProfileCardSets,
  getWorkspaceProfileCardSettings,
  profileCardImagePublicUrl,
} from '../../services/profileCardCatalogService';
import { getWorkspaceProfileCardSets } from '../../services/workspaceProfileCardService';
import type { AdminProfileCardSlot, ProfileCardSetSource } from '../../types/profileCard';
import { profileCardSetKey, profileCardSlotDisplayName } from '../../types/profileCard';
import {
  toMemberProfileStorageImageRef,
} from '../../utils/memberProfileDisplay';
import {
  loadAdminProfileCardSetsCache,
  loadWorkspaceProfileCardSettingsCache,
  loadWorkspaceProfileCardSetsCache,
} from '../../utils/profileCardCatalogCache';
import { preloadProfileCardImages } from '../../utils/profileCardImageCache';

export type ProfileImageSelectModalProps = {
  open: boolean;
  /** 열릴 때 자동 선택할 학생 — 없으면 첫 학생. 프로필 카드 잠금 해제 여부도 선택된 학생 기준 */
  initialMemberId?: string;
  /** 사진 프로필 탭용: 현재 그룹 멤버 */
  members?: ClassMemberRow[];
  workspaceId?: string;
  gradeId?: string;
  /** 사진 업로드/삭제 후 로컬 멤버 목록 반영 */
  onMembersPhotoChange?: (nextMembers: ClassMemberRow[]) => void;
  onClose: () => void;
  /** 헤더 중앙 스토어 버튼 — 있으면 표시 (현재 선택된 학생 id 전달) */
  onOpenStore?: (memberId: string) => void;
  /** 프로필 카드 보유 목록 재조회 트리거 (구매 후 증가) */
  unlocksRefreshKey?: number;
  /** 확인 — 변경된 학생만 { memberId: imageId } (왼쪽 학생 목록에서 학생별 지정 후 한 번에 반영) */
  onConfirm: (changes: Record<string, string>) => void;
};

/** 기본(svg) 탭: 10열×3행. 1~15 → 1~5열, 1~3행 / 16~30 → 6~10열, 1~3행 */
function buildProfileGridOrder(prefix: 'basic'): string[] {
  const cells: string[] = [];
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 5; col++) {
      cells.push(`${prefix}-${row * 5 + col + 1}`);
    }
    for (let col = 0; col < 5; col++) {
      cells.push(`${prefix}-${16 + row * 5 + col}`);
    }
  }
  return cells;
}

const BASIC_GRID_ORDER = buildProfileGridOrder('basic');

/** 비공개 버킷 사진은 signed URL을 비동기로 받아와야 하므로 별도 컴포넌트로 분리 */
const MemberPhotoPreviewImage: React.FC<{
  photoProfilePath?: string;
  photoProfileVersion?: number;
  alt: string;
}> = ({ photoProfilePath, photoProfileVersion, alt }) => {
  const signedUrl = useMemberPhotoDisplayUrl(photoProfilePath, photoProfileVersion);
  return (
    <CachedProfileImage
      imageId={signedUrl ?? undefined}
      alt={alt}
      fallback={
        <span className="cp-member-photo-profile-empty-ph">
          <span className="cp-member-photo-profile-empty-circle">+</span>
        </span>
      }
    />
  );
};

type SelectableProfileCardSet = {
  id: string;
  name: string;
  source: ProfileCardSetSource;
  slots: AdminProfileCardSlot[];
};

function formatProfileSetTabName(name: string): string {
  const trimmed = name.trim();
  return trimmed.endsWith('세트') ? trimmed : `${trimmed} 세트`;
}

function buildSelectableProfileCardSets(
  adminSets: Array<{ id: string; name: string; sortOrder: number; slots: AdminProfileCardSlot[] }>,
  adminSettings: Array<{ setId: string; enabled: boolean }>,
  userSets: Array<{
    id: string;
    name: string;
    sortOrder: number;
    enabled: boolean;
    slots: AdminProfileCardSlot[];
  }>
): SelectableProfileCardSet[] {
  const enabledAdminIds = new Set(adminSettings.filter((s) => s.enabled).map((s) => s.setId));
  return [
    ...adminSets
      .filter((s) => enabledAdminIds.has(s.id))
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((s) => ({
        id: s.id,
        name: s.name,
        source: 'admin' as const,
        slots: s.slots,
      })),
    ...userSets
      .filter((s) => s.enabled)
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((s) => ({
        id: s.id,
        name: s.name,
        source: 'workspace' as const,
        slots: s.slots,
      })),
  ];
}

function preloadSetsImages(sets: SelectableProfileCardSet[]): void {
  preloadProfileCardImages(
    sets.flatMap((s) =>
      s.slots.map((slot) => ({
        source: s.source,
        storagePath: slot.storagePath,
        imageVersion: slot.imageVersion,
      }))
    )
  );
}

const ProfileCardSlotThumb: React.FC<{
  source: ProfileCardSetSource;
  slot: AdminProfileCardSlot;
}> = ({ source, slot }) => {
  if (!slot.storagePath) return <span className="cp-profile-img-ph">?</span>;
  return (
    <ProfileCardCachedImage
      source={source}
      storagePath={slot.storagePath}
      imageVersion={slot.imageVersion}
      alt=""
    />
  );
};

const ProfileImageSelectModal: React.FC<ProfileImageSelectModalProps> = ({
  open,
  initialMemberId = '',
  members = [],
  workspaceId = '',
  gradeId = '',
  onMembersPhotoChange,
  onClose,
  onOpenStore,
  unlocksRefreshKey = 0,
  onConfirm,
}) => {
  /** 일괄 모드: 현재 선택된 학생 / 학생별 지정(미확정) 이미지 */
  const [bulkActiveId, setBulkActiveId] = useState<string>('');
  const [bulkPending, setBulkPending] = useState<Record<string, string>>({});
  const memberId = bulkActiveId;
  const bulkStripRef = useRef<HTMLDivElement | null>(null);
  /** 일괄 모드: 선택(자동 이동 포함)된 학생 블럭이 보이도록 스크롤 */
  useEffect(() => {
    if (!open || !bulkActiveId) return;
    const root = bulkStripRef.current;
    if (!root) return;
    const el = Array.from(root.querySelectorAll<HTMLElement>('[data-bulk-member-id]')).find(
      (n) => n.dataset.bulkMemberId === bulkActiveId
    );
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [open, bulkActiveId]);
  const [selectedImageId, setSelectedImageId] = useState(DEFAULT_MEMBER_PROFILE_IMAGE);
  const [imageErrors, setImageErrors] = useState<Set<string>>(() => new Set());
  /** 'A'=기본, 'photo'=사진 프로필, 그 외에는 프로필 카드 세트 id */
  const [activeSet, setActiveSet] = useState<string>('A');
  const [displayMode, setDisplayMode] = useState<MemberProfileDisplayMode>('svg');
  const [photoMembers, setPhotoMembers] = useState<ClassMemberRow[]>([]);
  const [selectedMember, setSelectedMember] = useState<ClassMemberRow | null>(null);
  const [photoError, setPhotoError] = useState('');
  const [profileCardSets, setProfileCardSets] = useState<SelectableProfileCardSet[]>([]);
  const [ownedSlotIndexesBySet, setOwnedSlotIndexesBySet] = useState<Record<string, number[]>>({});
  const [profileCardsLoading, setProfileCardsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setImageErrors(new Set());
    setActiveSet('A');
    setSelectedMember(null);
    setPhotoError('');
    const first = members.find((m) => m.id === initialMemberId) ?? members[0];
    setBulkPending({});
    setBulkActiveId(first?.id ?? '');
    setSelectedImageId(normalizeProfileImageId(first?.profileImage));
    preloadProfileImages([DEFAULT_MEMBER_PROFILE_IMAGE, ...BASIC_GRID_ORDER]);
    if (workspaceId.trim() && gradeId.trim()) {
      setDisplayMode(readMemberProfileDisplayMode(workspaceId, gradeId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 열릴 때만 선택 상태 초기화
  }, [open, initialMemberId, workspaceId, gradeId]);

  useEffect(() => {
    if (!open) return;
    setPhotoMembers(members);
  }, [open, members]);

  /** DB path가 비었는데 Storage/자산 메타에 파일이 남아 있으면 재연결 */
  useEffect(() => {
    if (!open || !workspaceId.trim() || !gradeId.trim()) return;

    let cancelled = false;
    (async () => {
      try {
        const ws = workspaceId.trim();
        const gid = gradeId.trim();
        const missing = members.filter((m) => !(m.photoProfilePath ?? '').trim());

        // 고아 추정: 자산 메타는 있는데 class_members.path 가 비어 있는 경우
        const orphanInfo = await countOrphanMemberProfileAssets({
          workspaceId: ws,
          gradeId: gid,
        });
        if (cancelled) return;
        if (process.env.NODE_ENV === 'development' && orphanInfo.assetCount > 0) {
          console.info('[member-photo] assets', orphanInfo);
        }

        if (missing.length === 0 && orphanInfo.orphanCount === 0) return;

        const found = await findExistingMemberPhotoPaths({
          workspaceId: ws,
          gradeId: gid,
          memberIds:
            missing.length > 0
              ? missing.map((m) => m.id)
              : members.map((m) => m.id),
        });
        if (cancelled || found.size === 0) return;

        const version = Date.now();
        await Promise.all(
          Array.from(found.entries()).map(([memberId, path]) =>
            updateMemberPhotoProfile({
              workspaceId: ws,
              gradeId: gid,
              memberId,
              photoProfilePath: path,
              photoProfileVersion: version,
            })
          )
        );
        if (cancelled) return;

        const next = members.map((m) => {
          const path = found.get(m.id);
          if (!path) return m;
          return { ...m, photoProfilePath: path, photoProfileVersion: version };
        });
        setPhotoMembers(next);
        onMembersPhotoChange?.(next);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[member-photo] orphan relink failed', err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open 전환 시 복구
  }, [open, workspaceId, gradeId]);

  /** 프로필 카드: LS 캐시 즉시 표시 → head 버전 비교 후 필요 시만 풀 갱신 + 이미지 프리로드 */
  useEffect(() => {
    if (!open || !workspaceId.trim() || !memberId.trim()) {
      setProfileCardSets([]);
      setOwnedSlotIndexesBySet({});
      setProfileCardsLoading(false);
      return undefined;
    }

    const ws = workspaceId.trim();
    const adminCached = loadAdminProfileCardSetsCache();
    const settingsCached = loadWorkspaceProfileCardSettingsCache(ws);
    const userCached = loadWorkspaceProfileCardSetsCache(ws);
    const hasCatalogCache = Boolean(adminCached && settingsCached);

    if (hasCatalogCache) {
      const cachedSets = buildSelectableProfileCardSets(
        adminCached!.sets,
        settingsCached!.settings,
        userCached?.sets ?? []
      );
      setProfileCardSets(cachedSets);
      setProfileCardsLoading(false);
      preloadSetsImages(cachedSets);
    } else {
      setProfileCardsLoading(true);
    }

    let cancelled = false;
    Promise.all([
      getAdminProfileCardSets(),
      getWorkspaceProfileCardSettings(ws),
      getWorkspaceProfileCardSets(ws),
      fetchMemberProfileCardUnlocks(memberId),
    ])
      .then(([adminSets, adminSettings, userSets, unlocks]) => {
        if (cancelled) return;
        const enabled = buildSelectableProfileCardSets(adminSets, adminSettings, userSets);
        setProfileCardSets(enabled);
        preloadSetsImages(enabled);
        const owned: Record<string, number[]> = {};
        unlocks.forEach((u) => {
          const key = profileCardSetKey(u.sourceKind, u.setId);
          (owned[key] ??= []).push(u.slotIndex);
        });
        setOwnedSlotIndexesBySet(owned);
      })
      .catch(() => {
        if (cancelled) return;
        if (!hasCatalogCache) {
          setProfileCardSets([]);
          setOwnedSlotIndexesBySet({});
        }
      })
      .finally(() => {
        if (!cancelled) setProfileCardsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, workspaceId, memberId, unlocksRefreshKey]);

  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  const sortedPhotoMembers = useMemo(() => {
    const nums = getClassMemberDisplayNumbers(photoMembers);
    return photoMembers.map((m, index) => ({
      member: m,
      numberLabel: `${nums[index] ?? index + 1}번`,
    }));
  }, [photoMembers]);

  if (!open) return null;

  const isPhotoTab = activeSet === 'photo';
  const isBasicTab = activeSet === 'A';
  const activeProfileCardSet =
    !isPhotoTab && !isBasicTab
      ? profileCardSets.find((s) => profileCardSetKey(s.source, s.id) === activeSet) ?? null
      : null;

  const renderSvgProfileItem = (imageId: string) => {
    const isSelected = selectedImageId === imageId;
    const hasError = imageErrors.has(imageId);
    return (
      <button
        key={imageId}
        type="button"
        className={`cp-profile-img-item${isSelected ? ' is-selected' : ''}${hasError ? ' is-error' : ''}`}
        disabled={hasError}
        onClick={() => handleImageClick(imageId)}
        aria-label={imageId === DEFAULT_MEMBER_PROFILE_IMAGE ? '프로필 이미지 없음' : `프로필 ${imageId}`}
        aria-pressed={isSelected}
      >
        <span className="cp-profile-img-frame">
          <CachedProfileImage imageId={imageId} alt="" fallback={null} />
        </span>
        {isSelected && !hasError ? (
          <span className="cp-profile-img-check" aria-hidden>
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <circle cx="16" cy="16" r="16" fill="var(--color-accent-strong, #10504a)" />
              <path
                d="M10 16L14 20L22 12"
                stroke="#fff"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        ) : null}
      </button>
    );
  };

  const bulkImageOf = (id: string): string => {
    if (bulkPending[id] != null) return bulkPending[id];
    const m = members.find((x) => x.id === id);
    return normalizeProfileImageId(m?.profileImage);
  };

  const selectBulkMember = (id: string) => {
    setBulkActiveId(id);
    setSelectedImageId(bulkImageOf(id));
  };

  /** 일괄 모드: 현재 학생에게 지정 후 다음 학생으로 자동 이동 */
  const assignBulk = (imageId: string) => {
    if (!bulkActiveId) return;
    const next = { ...bulkPending, [bulkActiveId]: imageId };
    setBulkPending(next);
    const idx = members.findIndex((m) => m.id === bulkActiveId);
    const following = idx >= 0 ? members[idx + 1] : undefined;
    if (following) {
      setBulkActiveId(following.id);
      setSelectedImageId(
        next[following.id] ?? normalizeProfileImageId(following.profileImage)
      );
    } else {
      setSelectedImageId(imageId);
    }
  };

  const handleImageClick = (imageId: string, locked = false) => {
    if (imageErrors.has(imageId) || locked) return;
    assignBulk(imageId);
  };

  const handleConfirm = () => {
    const changes: Record<string, string> = {};
    Object.entries(bulkPending).forEach(([id, img]) => {
      const m = members.find((x) => x.id === id);
      if (m && normalizeProfileImageId(m.profileImage) !== img) changes[id] = img;
    });
    if (
      Object.keys(changes).length > 0 &&
      workspaceId.trim() &&
      gradeId.trim() &&
      displayMode === 'photo'
    ) {
      writeMemberProfileDisplayMode(workspaceId.trim(), gradeId.trim(), 'svg');
      setDisplayMode('svg');
    }
    onConfirm(changes);
    onClose();
  };

  const handleClearProfileImage = () => {
    assignBulk(DEFAULT_MEMBER_PROFILE_IMAGE);
  };

  const handleCancel = () => {
    setBulkPending({});
    onClose();
  };

  const handleDisplayModeChange = (mode: MemberProfileDisplayMode) => {
    if (mode === displayMode) return;
    setDisplayMode(mode);
    if (workspaceId.trim() && gradeId.trim()) {
      writeMemberProfileDisplayMode(workspaceId, gradeId, mode);
    }
    if (mode === 'photo') {
      preloadMemberPhotoImages(
        photoMembers.map((m) => ({
          path: m.photoProfilePath,
          version: m.photoProfileVersion,
        }))
      );
    }
  };

  const applyMembersLocal = (next: ClassMemberRow[]) => {
    setPhotoMembers(next);
    onMembersPhotoChange?.(next);
  };

  const handleUploadSave = async (blob: Blob) => {
    if (!selectedMember || !workspaceId.trim() || !gradeId.trim()) return;
    const targetMemberId = selectedMember.id;
    const photoProfileVersion = Date.now();
    const uploaded = await uploadMemberPhoto({
      workspaceId,
      gradeId,
      memberId: targetMemberId,
      blob,
    });
    await updateMemberPhotoProfile({
      workspaceId,
      gradeId,
      memberId: targetMemberId,
      photoProfilePath: uploaded.path,
      photoProfileVersion,
    });
    applyMembersLocal(
      photoMembers.map((m) =>
        m.id === targetMemberId
          ? {
              ...m,
              photoProfilePath: uploaded.path,
              photoProfileVersion,
            }
          : m
      )
    );
  };

  const handleDeletePhoto = async (member: ClassMemberRow) => {
    if (!workspaceId.trim() || !gradeId.trim()) return;
    try {
      await deleteMemberPhoto({
        workspaceId,
        gradeId,
        memberId: member.id,
        storagePath: member.photoProfilePath,
      });
      await updateMemberPhotoProfile({
        workspaceId,
        gradeId,
        memberId: member.id,
      });
      applyMembersLocal(
        photoMembers.map((m) =>
          m.id === member.id
            ? {
                ...m,
                photoProfilePath: undefined,
                photoProfileVersion: undefined,
              }
            : m
        )
      );
    } catch (e) {
      console.error(e);
      setPhotoError('프로필 사진 삭제에 실패했습니다.');
    }
  };

  return createPortal(
    <>
      <div className="cp-profile-img-modal-overlay" onClick={handleCancel} role="presentation">
        <div
          className={`cp-profile-img-modal is-bulk${isPhotoTab ? ' is-photo-tab' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cp-profile-img-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
        <div className="cp-profile-img-modal-header">
          <h3 id="cp-profile-img-modal-title">프로필 이미지 설정</h3>
          {onOpenStore ? (
            <button
              type="button"
              className="cp-profile-img-modal-store-btn"
              onClick={() => bulkActiveId && onOpenStore(bulkActiveId)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.8}
                stroke="currentColor"
                width="16"
                height="16"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349m-16.5 11.65V9.35m0 0a3.001 3.001 0 003.75-.615A2.993 2.993 0 009.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 002.25 1.016c.896 0 1.7-.393 2.25-1.016a3.001 3.001 0 003.75.614m-16.5 0a3.004 3.004 0 01-.621-4.72L4.318 3.44A1.5 1.5 0 015.378 3h13.243a1.5 1.5 0 011.06.44l1.19 1.189a3 3 0 01-.621 4.72m-13.5 8.65h3.75a.75.75 0 00.75-.75V13.5a.75.75 0 00-.75-.75H6.75a.75.75 0 00-.75.75v3.75c0 .415.336.75.75.75z"
                />
              </svg>
              스토어
            </button>
          ) : (
            <span className="cp-profile-img-modal-header-spacer" aria-hidden />
          )}
          <button type="button" className="cp-profile-img-modal-close" onClick={handleCancel} aria-label="닫기">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {members.length > 0 ? (
          <div
            ref={bulkStripRef}
            className="cp-profile-img-bulk-strip"
            role="listbox"
            aria-label="학생 선택"
          >
            <div className="cp-pin-store-buyers-grid cp-profile-img-bulk-grid">
              {sortedPhotoMembers.map(({ member, numberLabel }) => {
                const active = member.id === bulkActiveId;
                const changed =
                  bulkPending[member.id] != null &&
                  bulkPending[member.id] !== normalizeProfileImageId(member.profileImage);
                const name = displayClassMemberName(member) || '';
                const num = numberLabel.replace(/번$/, '');
                return (
                  <button
                    key={member.id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`cp-pin-store-buyer cp-profile-img-bulk-stu${
                      active ? ' is-selected' : ''
                    }${changed ? ' is-changed' : ''}`}
                    data-bulk-member-id={member.id}
                    onClick={() => selectBulkMember(member.id)}
                    aria-label={`${numberLabel} ${name || '이름 없음'}${changed ? ' (변경 예정)' : ''}`}
                  >
                    <span className="cp-pin-store-buyer-avatar">
                      <CachedProfileImage
                        imageId={bulkImageOf(member.id)}
                        alt=""
                        className="cp-pin-store-buyer-avatar-img"
                        fallback={
                          <span className="cp-pin-store-buyer-fallback">
                            {(name || '?').slice(0, 1)}
                          </span>
                        }
                      />
                    </span>
                    <span className="cp-pin-store-buyer-id">
                      <span className="cp-pin-store-buyer-no">
                        {/^\d+$/.test(num) ? num.padStart(2, '0') : num}
                      </span>
                      <span className="cp-pin-store-buyer-name">{name || '(이름 없음)'}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="cp-profile-img-tabs" role="tablist" aria-label="프로필 세트">
          <button
            type="button"
            role="tab"
            aria-selected={activeSet === 'A'}
            className={`cp-profile-img-tab${activeSet === 'A' ? ' is-active' : ''}`}
            onClick={() => setActiveSet('A')}
          >
            기본 세트
          </button>
          {profileCardSets.map((set) => {
            const key = profileCardSetKey(set.source, set.id);
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeSet === key}
                className={`cp-profile-img-tab${activeSet === key ? ' is-active' : ''}`}
                data-tutorial-profile-set={set.source === 'admin' ? 'locked-admin' : 'user'}
                onClick={() => setActiveSet(key)}
              >
                <span
                  className={`cp-admin-learning-template-dot ${
                    set.source === 'admin'
                      ? 'cp-admin-learning-template-dot--default'
                      : 'cp-admin-learning-template-dot--user'
                  }`}
                  aria-hidden
                />
                {formatProfileSetTabName(set.name)}
              </button>
            );
          })}
          <button
            type="button"
            role="tab"
            aria-selected={isPhotoTab}
            className={`cp-profile-img-tab cp-profile-img-tab-photo${isPhotoTab ? ' is-active' : ''}`}
            onClick={() => setActiveSet('photo')}
          >
            사진 프로필
          </button>
        </div>

        {isPhotoTab ? (
          <div className="cp-member-photo-profile-panel">
            <div className="cp-member-photo-profile-panel-head">
              <h4 className="cp-member-photo-profile-panel-title">멤버 사진 프로필 설정</h4>
              <div
                className={`cp-member-photo-profile-toggle${displayMode === 'photo' ? ' is-on' : ''}`}
                role="switch"
                aria-checked={displayMode === 'photo'}
                aria-label="프로필 이미지를 사진 프로필로 표시하기"
                tabIndex={0}
                onClick={() =>
                  handleDisplayModeChange(displayMode === 'photo' ? 'svg' : 'photo')
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleDisplayModeChange(displayMode === 'photo' ? 'svg' : 'photo');
                  }
                }}
              >
                <span
                  className={`cp-member-photo-profile-toggle-label${
                    displayMode !== 'photo' ? ' is-active' : ''
                  }`}
                >
                  OFF
                </span>
                <span
                  className={`cp-member-photo-profile-toggle-label${
                    displayMode === 'photo' ? ' is-active' : ''
                  }`}
                >
                  ON
                </span>
              </div>
            </div>

            {photoError ? (
              <p className="cp-member-photo-profile-error" role="alert">
                {photoError}
              </p>
            ) : null}

            {sortedPhotoMembers.length === 0 ? (
              <p className="cp-member-photo-profile-empty">등록된 멤버가 없습니다.</p>
            ) : (
              <div className="cp-member-photo-profile-grid">
                {sortedPhotoMembers.map(({ member, numberLabel }) => (
                  <div
                    key={member.id}
                    className="cp-member-photo-profile-card"
                    role="button"
                    tabIndex={0}
                    aria-label={`${numberLabel} ${displayClassMemberName(member) || '-'} 사진 프로필 설정`}
                    onClick={() => setSelectedMember(member)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedMember(member);
                      }
                    }}
                  >
                    <div className="cp-member-photo-profile-card-media">
                      {member.photoProfilePath ? (
                        <MemberPhotoPreviewImage
                          photoProfilePath={member.photoProfilePath}
                          photoProfileVersion={member.photoProfileVersion}
                          alt={`${displayClassMemberName(member)} 프로필`}
                        />
                      ) : (
                        <span className="cp-member-photo-profile-empty-ph">
                          <span className="cp-member-photo-profile-empty-circle">+</span>
                        </span>
                      )}
                    </div>
                    <div className="cp-member-photo-profile-card-info">{numberLabel}</div>
                    <div className="cp-member-photo-profile-card-info">
                      {displayClassMemberName(member) || '-'}
                    </div>
                    {member.photoProfilePath ? (
                      <button
                        type="button"
                        className="cp-member-photo-profile-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDeletePhoto(member);
                        }}
                      >
                        사진 삭제
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="cp-profile-img-grid is-basic">
              {isBasicTab
                ? BASIC_GRID_ORDER.map((imageId) => renderSvgProfileItem(imageId))
                : activeProfileCardSet
                  ? activeProfileCardSet.slots
                      .slice()
                      .sort((a, b) => a.slotIndex - b.slotIndex)
                      .map((slot) => {
                        const hasImage = Boolean(slot.storagePath);
                        const imageRef = !hasImage
                          ? null
                          : activeProfileCardSet.source === 'workspace'
                            ? toMemberProfileStorageImageRef(slot.storagePath as string)
                            : profileCardImagePublicUrl(
                                slot.storagePath as string,
                                slot.imageVersion
                              );
                        const ownedKey = profileCardSetKey(
                          activeProfileCardSet.source,
                          activeProfileCardSet.id
                        );
                        const owned = (ownedSlotIndexesBySet[ownedKey] ?? []).includes(
                          slot.slotIndex
                        );
                        const isLocked = !hasImage || !owned;
                        const isSelected = imageRef != null && selectedImageId === imageRef;
                        const hasError = imageRef != null && imageErrors.has(imageRef);
                        return (
                          <button
                            key={slot.id}
                            type="button"
                            className={`cp-profile-img-item${isSelected ? ' is-selected' : ''}${
                              hasError ? ' is-error' : ''
                            }${isLocked ? ' is-locked' : ''}${hasImage ? ' has-preview' : ''}`}
                            disabled={isLocked || hasError}
                            onClick={() => imageRef && handleImageClick(imageRef, isLocked)}
                            aria-label={
                              isLocked
                                ? `잠긴 프로필 카드 ${profileCardSlotDisplayName(slot)}`
                                : `프로필 카드 ${profileCardSlotDisplayName(slot)}`
                            }
                            aria-pressed={isSelected}
                          >
                            {hasImage ? (
                              <span
                                className={`cp-profile-img-frame${isLocked ? ' is-locked-preview' : ''}`}
                              >
                                <ProfileCardSlotThumb
                                  source={activeProfileCardSet.source}
                                  slot={slot}
                                />
                              </span>
                            ) : null}
                            {isLocked ? (
                              <span className="cp-profile-img-locked" aria-hidden>
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                                  <rect x="5" y="10" width="14" height="11" rx="2" fill="#6c757d" />
                                  <path
                                    d="M8 10V7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7V10"
                                    stroke="#6c757d"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </span>
                            ) : null}
                            {isSelected && !hasError && !isLocked ? (
                              <span className="cp-profile-img-check" aria-hidden>
                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                                  <circle
                                    cx="16"
                                    cy="16"
                                    r="16"
                                    fill="var(--color-accent-strong, #10504a)"
                                  />
                                  <path
                                    d="M10 16L14 20L22 12"
                                    stroke="#fff"
                                    strokeWidth="4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </span>
                            ) : null}
                          </button>
                        );
                      })
                  : null}
            </div>

            {!isBasicTab && activeProfileCardSet && profileCardsLoading ? (
              <p className="cp-profile-img-hint">불러오는 중…</p>
            ) : null}

            <div className="cp-profile-img-actions">
              <button type="button" className="cp-profile-img-btn is-cancel" onClick={handleCancel}>
                취소
              </button>
              <button
                type="button"
                className="cp-profile-img-btn is-clear"
                onClick={handleClearProfileImage}
              >
                프로필 이미지 없음
              </button>
              <button type="button" className="cp-profile-img-btn is-confirm" onClick={handleConfirm}>
                확인
              </button>
            </div>
          </>
        )}
        </div>
      </div>

      {selectedMember ? (
        <MemberProfileImageUploadModal
          open={Boolean(selectedMember)}
          memberName={displayClassMemberName(selectedMember).trim() || '멤버'}
          onClose={() => setSelectedMember(null)}
          onSave={handleUploadSave}
        />
      ) : null}
    </>,
    document.body
  );
};

export default ProfileImageSelectModal;
